import {inlineError, tracer} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'

/**
 * @implements UseCase
 */
class GetSearchPaginationUrlAdsUseCase extends UseCase {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {GetSearchPaginationUrlAdsService} deps.getSearchPaginationUrlAdsService
   * @param {function} deps.searchRequestValueObjectFactory
   */
  constructor({config, searchRequestValueObjectFactory, getSearchPaginationUrlAdsService}) {
    super()

    this._config = config
    this._pageConfig = this._config.get('page')
    this._defaultPaginationPlaceholder = this._pageConfig.PAGINATION_PLACEHOLDER
    this._defaultSortOrder = this._config.get('ORDER').DESC
    this._defaultSortBy = this._config.get('SORT_FIELDS').RELEVANCE

    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory
    this._getSearchPaginationUrlAdsService = getSearchPaginationUrlAdsService
  }

  /**
   * @param {Object} params
   * @param {String} params.paginationPlaceholder
   * @param {String} params.pageName
   * @param {Object} params.search
   * @param {Number} params.search.page
   * @param {String} params.search.sortBy
   * @param {Number} params.search.sortOrder
   * @return {Promise<Object, Error>} search params object
   * @return {Object<url: String>}
   */
  @tracer({metric: 'GetSearchPaginationUrlAdsUseCase#execute'})
  @inlineError
  async execute({
    dealerSlug,
    listingSectionId,
    pageName,
    paginationPlaceholder = this._defaultPaginationPlaceholder,
    queryParams = {},
    search: {page = 1, sortBy = this._defaultSortBy, sortOrder = this._defaultSortOrder, ...filters} = {},
    url
  }) {
    const queries = Object.keys(queryParams)
    const hasDealerSlug = Boolean(dealerSlug)
    const hasNoQueryParams = Boolean(!queries.length)
    const hasOnlyPgQueryParam = Boolean(queries.length === 1) && Boolean(queryParams.pg)
    const hasUrl = Boolean(url)
    const hasSemanticUrl = hasUrl && (hasNoQueryParams || hasOnlyPgQueryParam)

    // If the url has no queryParams or the only queryParam is `pg`,
    // build the paging url with the current url,
    // so it can use the semantic url if needed
    if (!hasDealerSlug && hasSemanticUrl) return `${url}${this._pageConfig.QUESTION_MARK}pg=${paginationPlaceholder}`

    const searchRequestObjectVO = this._searchRequestValueObjectFactory({
      page,
      sortBy,
      sortOrder,
      ...filters
    })

    return this._getSearchPaginationUrlAdsService
      .execute({
        dealerSlug,
        listingSectionId,
        pageName,
        paginationPlaceholder,
        searchRequestObjectVO
      })
      .then(([error, urlVO]) => {
        if (error) {
          return Promise.reject(error)
        }

        const {url} = urlVO.toJSON()

        const paginationParam = url.indexOf('?') > -1 ? this._pageConfig.AMPERSAND_MARK : this._pageConfig.QUESTION_MARK

        return `${url}${paginationParam}${this._pageConfig.PAGINATION_PATTERN}${paginationPlaceholder}`
      })
  }
}

export default GetSearchPaginationUrlAdsUseCase
